/**
 * <scroll-progress> — barre de progression pour carrousels à défilement.
 *
 * Affiche une piste fine avec un curseur (thumb) dont la largeur = fraction
 * visible du carrousel et la position = progression du scroll. Montre d'un coup
 * d'œil qu'il reste du contenu à faire défiler. Se masque si non scrollable.
 *
 * Le scroller cible est résolu via l'attribut data-scroller (sélecteur CSS),
 * cherché dans le plus proche [data-scroll-progress-scope] ou le parent direct ;
 * fallback : le <slideshow-slides> du <slideshow-component> englobant.
 */
class ScrollProgress extends HTMLElement {
  connectedCallback() {
    this.scroller = this.resolveScroller();
    if (!this.scroller) return;

    this._onScroll = () => this._schedule();
    this.scroller.addEventListener("scroll", this._onScroll, { passive: true });

    if ("ResizeObserver" in window) {
      this._ro = new ResizeObserver(() => this._schedule());
      this._ro.observe(this.scroller);
    } else {
      this._onResize = () => this._schedule();
      window.addEventListener("resize", this._onResize);
    }

    this._schedule();
    // Les images en lazy-load changent scrollWidth : recalcule au load.
    window.addEventListener("load", () => this._schedule(), { once: true });
  }

  disconnectedCallback() {
    if (this.scroller && this._onScroll)
      this.scroller.removeEventListener("scroll", this._onScroll);
    if (this._ro) this._ro.disconnect();
    if (this._onResize) window.removeEventListener("resize", this._onResize);
  }

  resolveScroller() {
    const sel = this.getAttribute("data-scroller");
    if (sel) {
      const scope =
        this.closest("[data-scroll-progress-scope]") ||
        this.parentElement ||
        document;
      const el = scope.querySelector(sel);
      if (el) return el;
    }
    const ss = this.closest("slideshow-component");
    if (ss) return ss.querySelector("slideshow-slides");
    return null;
  }

  _schedule() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._ticking = false;
      this.update();
    });
  }

  update() {
    const s = this.scroller;
    const max = s.scrollWidth - s.clientWidth;
    const scrollable = max > 2;
    this.toggleAttribute("data-hidden", !scrollable);
    if (!scrollable) return;

    let thumb = s.clientWidth / s.scrollWidth;
    thumb = Math.min(1, Math.max(0.14, thumb));
    const ratio = Math.min(1, Math.max(0, s.scrollLeft / max));

    this.style.setProperty("--sp-thumb", (thumb * 100).toFixed(2) + "%");
    this.style.setProperty(
      "--sp-left",
      (ratio * (1 - thumb) * 100).toFixed(2) + "%",
    );
  }
}

if (!customElements.get("scroll-progress")) {
  customElements.define("scroll-progress", ScrollProgress);
}
